/**
 * @file OperationsWeeklyDataCheck.jsx — OPERATIONS department — Weekly Data Check.
 * @description The internal check the team runs every week BEFORE the log
 *              sheets / invoices go out: is every machine's data for the week
 *              right? The server reads each machine's week exactly the way the
 *              invoice PDF reads it (engine on/off from RPM, the hour meter,
 *              frames per day, the contract line) and raises a finding
 *              wherever the invoice would print something wrong — a silent
 *              day on rent, an hour meter that never moved, a meter going
 *              backwards, no contract covering the week… The employee then
 *              records a verdict per machine (Verified / Issue / Fixed, with a
 *              note) and signs the week off. Who checked what, and when, is
 *              kept, so next week's checker can see last week's issues.
 *
 * This file belongs to the OPERATIONS department. Departments never share page
 * files: a change for OPERATIONS is made HERE and cannot leak into another
 * department's screens. Backend: /api/reports/weekly-check*.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, Stack, Chip, Button, IconButton, Tooltip, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Skeleton, TextField, InputAdornment, Collapse, ToggleButton, ToggleButtonGroup,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Search as SearchIcon,
    ChevronLeft as PrevIcon, ChevronRight as NextIcon,
    KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
    CheckCircle as OkIcon, ReportProblem as WarnIcon, Error as FailIcon, Info as InfoIcon,
    TaskAlt as VerifiedIcon, Flag as IssueIcon, Build as FixedIcon, Undo as UndoIcon,
    Download as DownloadIcon, OpenInNew as OpenIcon, VerifiedUser as SignIcon, Email as EmailIcon,
} from '@mui/icons-material';
import reportsService from '../../../services/reportsService';
import { formatToIST } from '../../../utils/dataUtils';

// Colour never travels alone — every state carries its word.
const AUTO = {
    ok:   { color: 'success', label: 'Looks right', Icon: OkIcon },
    warn: { color: 'warning', label: 'Check',       Icon: WarnIcon },
    fail: { color: 'error',   label: 'Problem',     Icon: FailIcon },
};
const SEV = {
    error:   { color: 'error',   Icon: FailIcon },
    warning: { color: 'warning', Icon: WarnIcon },
    info:    { color: 'info',    Icon: InfoIcon },
};
const VERDICT = {
    OK:    { color: 'success', label: 'Verified', Icon: VerifiedIcon },
    ISSUE: { color: 'error',   label: 'Issue',    Icon: IssueIcon },
    FIXED: { color: 'info',    label: 'Fixed',    Icon: FixedIcon },
};
// Legend for the DayStrip cells (same tones as DayStrip below).
const DAY_LEGEND = [
    ['success.main', 'Ran'],
    ['action.disabledBackground', 'Idle'],
    ['error.main', 'No data, on rent'],
    ['warning.main', 'Check'],
];
// Each filter is a test on one machine row; the bar shows how many pass it.
const FILTERS = [
    { key: 'all',      label: 'All',          test: () => true },
    { key: 'flagged',  label: 'Flagged',      test: (m) => m.auto !== 'ok' },
    { key: 'pending',  label: 'Not reviewed', test: (m) => !m.review },
    { key: 'verified', label: 'Verified',     test: (m) => m.review?.verdict === 'OK' },
    { key: 'issues',   label: 'Issues',       test: (m) => m.review?.verdict === 'ISSUE' },
    { key: 'fixed',    label: 'Fixed',        test: (m) => m.review?.verdict === 'FIXED' },
];

const weekLabel = (ws, we) => ws && we
    ? `${formatToIST(`${ws}T00:00:00+05:30`, 'dd MMM')} – ${formatToIST(`${we}T00:00:00+05:30`, 'dd MMM yyyy')}`
    : '—';
const h = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}h`);
/** apiClient throws `API Error: 502 Bad Gateway - {"error":"…"}` — show the sentence. */
const friendly = (e) => {
    const m = String(e?.message || '');
    try {
        const parsed = JSON.parse(m.slice(m.indexOf('{')));
        if (parsed?.error) return parsed.error;
    } catch { /* not an envelope */ }
    return m || 'Something went wrong';
};

/** Seven small cells — the week at a glance: ran / idle / silent / future. */
const DayStrip = ({ days }) => (
    <Stack direction="row" spacing={0.4}>
        {days.map(d => {
            const silent = !d.future && d.frames === 0;
            const ran = d.span_h > 0 || d.delta_h > 0;
            const bad = d.flags.length > 0 && !d.flags.every(f => f === 'SILENT' && !d.on_rent);
            const tone = d.future ? 'transparent'
                : silent ? (d.on_rent ? 'error.main' : 'warning.light')
                : bad ? 'warning.main'
                : ran ? 'success.main' : 'action.disabledBackground';
            const tip = d.future ? `${d.dow} ${d.date} — not yet`
                : silent ? `${d.dow} ${d.date} — no data${d.on_rent ? ' (on rent)' : ''}`
                : `${d.dow} ${d.date} — ${d.frames} frames · on ${d.first_on || '—'}→${d.last_on || '—'} (${h(d.span_h)}) · meter ${h(d.delta_h)}${d.flags.length ? ` · ${d.flags.join(', ')}` : ''}`;
            return (
                <Tooltip key={d.date} title={tip}>
                    <Box sx={{
                        width: 18, height: 18, borderRadius: '4px', bgcolor: tone,
                        border: '1px solid', borderColor: d.future ? 'divider' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 800, color: d.future ? 'text.disabled' : '#fff',
                    }}>
                        {d.dow[0]}
                    </Box>
                </Tooltip>
            );
        })}
    </Stack>
);

const MachineRow = ({ m, weekStart, onReview, onClear, onOpen, busy }) => {
    const [open, setOpen] = useState(m.auto === 'fail');
    const [note, setNote] = useState(m.review?.note || '');
    useEffect(() => { setNote(m.review?.note || ''); }, [m.review?.note]);

    const a = AUTO[m.auto] || AUTO.ok;
    const v = m.review ? VERDICT[m.review.verdict] : null;
    const contract = m.contracts[0];
    const cols = 8;

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' }, bgcolor: m.auto === 'fail' && !m.review ? 'error.light' : undefined }}>
                <TableCell sx={{ width: 40 }}>
                    <IconButton size="small" onClick={() => setOpen(o => !o)} aria-label={open ? 'Hide details' : 'Show details'}>
                        {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={800}>{m.serial_number || m.equipment_id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {m.model || '—'}{m.serial_number ? ` · ${m.equipment_id}` : ''}{m.source === 'sinoboom' ? ' · Sinoboom' : ''}
                    </Typography>
                </TableCell>
                <TableCell>
                    {contract ? (
                        <>
                            <Typography variant="body2" fontWeight={600} noWrap>{contract.customer || contract.rental_contract_id}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {contract.rental_contract_id}{contract.site ? ` · ${contract.site}` : ''}{m.contracts.length > 1 ? ` · +${m.contracts.length - 1} more` : ''}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="caption" color="text.disabled" fontWeight={600}>No contract this week</Typography>
                    )}
                </TableCell>
                <TableCell><DayStrip days={m.days} /></TableCell>
                <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>{h(m.week.meter_h)}</Typography>
                    <Typography variant="caption" color="text.secondary">on {h(m.week.ran_h)}</Typography>
                </TableCell>
                <TableCell>
                    <Chip size="small" color={a.color} icon={<a.Icon sx={{ fontSize: '15px !important' }} />}
                        label={m.findings.length ? `${a.label} · ${m.findings.length}` : a.label}
                        variant={m.auto === 'ok' ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 700, height: 24 }} />
                </TableCell>
                <TableCell>
                    {v ? (
                        <Tooltip title={`${m.review.checked_by_name || 'someone'} · ${formatToIST(m.review.checked_at, 'dd MMM, hh:mm a')}${m.review.note ? ` — ${m.review.note}` : ''}`}>
                            <Chip size="small" color={v.color} icon={<v.Icon sx={{ fontSize: '15px !important' }} />} label={v.label} sx={{ fontWeight: 700, height: 24 }} />
                        </Tooltip>
                    ) : (
                        <Chip size="small" variant="outlined" label="Not reviewed" sx={{ fontWeight: 700, height: 24, color: 'text.secondary' }} />
                    )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Verified — the week's data is right">
                        <span><IconButton size="small" color="success" disabled={busy} onClick={() => onReview(m.equipment_id, 'OK', note)}><VerifiedIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Issue — something is wrong (add a note below)">
                        <span><IconButton size="small" color="error" disabled={busy} onClick={() => { setOpen(true); onReview(m.equipment_id, 'ISSUE', note); }}><IssueIcon fontSize="small" /></IconButton></span>
                    </Tooltip>
                    <Tooltip title="Open this machine">
                        <IconButton size="small" onClick={() => onOpen(m.equipment_id)}><OpenIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell sx={{ py: 0, borderBottom: open ? 1 : 0, borderColor: 'divider' }} colSpan={cols}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '5fr 7fr' }, gap: 3 }}>
                            <Box>
                                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Findings
                                </Typography>
                                {m.findings.length === 0 ? (
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                                        <OkIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                        <Typography variant="body2">No findings</Typography>
                                    </Stack>
                                ) : m.findings.map((f, i) => {
                                    const s = SEV[f.severity] || SEV.info;
                                    return (
                                        <Stack key={i} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.6 }}>
                                            <s.Icon sx={{ fontSize: 16, color: `${s.color}.main`, mt: '2px' }} />
                                            <Box>
                                                <Typography variant="body2">{f.message}</Typography>
                                                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>{f.code}</Typography>
                                            </Box>
                                        </Stack>
                                    );
                                })}

                                <Divider sx={{ my: 1.5 }} />
                                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Verdict
                                </Typography>
                                <TextField
                                    size="small" fullWidth multiline minRows={2} sx={{ mt: 1 }}
                                    placeholder="Note"
                                    value={note} onChange={e => setNote(e.target.value)}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                                    <Button size="small" variant="contained" color="success" startIcon={<VerifiedIcon />} disabled={busy}
                                        onClick={() => onReview(m.equipment_id, 'OK', note)} sx={{ textTransform: 'none', fontWeight: 700 }}>Verified</Button>
                                    <Button size="small" variant="outlined" color="error" startIcon={<IssueIcon />} disabled={busy}
                                        onClick={() => onReview(m.equipment_id, 'ISSUE', note)} sx={{ textTransform: 'none', fontWeight: 700 }}>Issue</Button>
                                    <Button size="small" variant="outlined" color="info" startIcon={<FixedIcon />} disabled={busy}
                                        onClick={() => onReview(m.equipment_id, 'FIXED', note)} sx={{ textTransform: 'none', fontWeight: 700 }}>Fixed</Button>
                                    {m.review && (
                                        <Button size="small" color="inherit" startIcon={<UndoIcon />} disabled={busy}
                                            onClick={() => onClear(m.equipment_id)} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>Clear</Button>
                                    )}
                                </Stack>
                                {m.review && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        {VERDICT[m.review.verdict]?.label} by {m.review.checked_by_name || '—'} · {formatToIST(m.review.checked_at, 'dd MMM yyyy, hh:mm a')}
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                                    Day by day
                                </Typography>
                                <TableContainer sx={{ mt: 0.5 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Day', 'Frames', 'Engine on', 'Engine off', 'On', 'Start hrs', 'End hrs', 'Meter', 'Fuel', 'GPS', 'Rent', 'Flags'].map(t => (
                                                    <TableCell key={t} sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', whiteSpace: 'nowrap' }}
                                                        align={['Frames', 'On', 'Start hrs', 'End hrs', 'Meter', 'Fuel'].includes(t) ? 'right' : 'left'}>{t.toUpperCase()}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {m.days.map(d => {
                                                const silent = !d.future && d.frames === 0;
                                                const mono = { fontFamily: 'monospace', fontSize: 12 };
                                                return (
                                                    <TableRow key={d.date} sx={{ opacity: d.future ? 0.45 : 1, bgcolor: d.flags.length && !d.future ? 'warning.light' : undefined }}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Typography variant="body2" fontWeight={700}>{d.dow}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{d.date.slice(5)}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right" sx={mono}>{d.future ? '—' : d.frames}</TableCell>
                                                        <TableCell sx={mono}>{d.first_on || '—'}</TableCell>
                                                        <TableCell sx={mono}>{d.last_on || '—'}</TableCell>
                                                        <TableCell align="right" sx={mono}>{d.future || silent ? '—' : h(d.span_h)}</TableCell>
                                                        <TableCell align="right" sx={mono}>{d.min_eng == null ? '—' : d.min_eng.toFixed(1)}</TableCell>
                                                        <TableCell align="right" sx={mono}>{d.max_eng == null ? '—' : d.max_eng.toFixed(1)}</TableCell>
                                                        <TableCell align="right" sx={{ ...mono, fontWeight: 700 }}>{d.future || silent ? '—' : h(d.delta_h)}</TableCell>
                                                        <TableCell align="right" sx={mono}>{d.future ? '—' : d.fuel_readings}</TableCell>
                                                        <TableCell>{d.future ? '—' : d.gps ? 'yes' : <Typography variant="caption" color="warning.dark" fontWeight={700}>none</Typography>}</TableCell>
                                                        <TableCell>{d.on_rent ? <Typography variant="caption" color="success.dark" fontWeight={700}>on rent</Typography> : <Typography variant="caption" color="text.disabled">—</Typography>}</TableCell>
                                                        <TableCell>
                                                            {d.flags.map(f => (
                                                                <Chip key={f} size="small" label={f} color={f === 'SILENT' && d.on_rent ? 'error' : f === 'SILENT' ? 'default' : 'warning'}
                                                                    variant="outlined" sx={{ height: 18, fontSize: 9, fontWeight: 800, mr: 0.5 }} />
                                                            ))}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const OperationsWeeklyDataCheck = () => {
    const navigate = useNavigate();
    const [week, setWeek] = useState(null);           // null → server picks the last complete week
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('all');
    const [signOpen, setSignOpen] = useState(false);
    const [signNote, setSignNote] = useState('');
    const [toast, setToast] = useState(null);
    const [mailing, setMailing] = useState(false);

    const load = useCallback(async (force = false) => {
        setLoading(true); setError(null);
        try {
            const res = await reportsService.getWeeklyCheck(week, force);
            setData(res || null);
        } catch (e) {
            console.error('Weekly data check fetch failed:', e);
            setError(friendly(e));
        } finally {
            setLoading(false);
        }
    }, [week]);

    useEffect(() => { load(); }, [load]);

    const patchReview = (equipment_id, review) =>
        setData(d => d && ({
            ...d,
            machines: d.machines.map(m => m.equipment_id === equipment_id ? { ...m, review } : m),
        }));

    const onReview = async (equipment_id, verdict, note) => {
        if (!data) return;
        setBusy(true);
        try {
            const res = await reportsService.reviewWeeklyMachine({ week_start: data.week_start, equipment_id, verdict, note });
            patchReview(equipment_id, res.review);
            setToast(`${VERDICT[verdict].label}: ${equipment_id}`);
        } catch (e) {
            setError(friendly(e));
        } finally { setBusy(false); }
    };
    const onClear = async (equipment_id) => {
        if (!data) return;
        setBusy(true);
        try {
            await reportsService.clearWeeklyReview(data.week_start, equipment_id);
            patchReview(equipment_id, null);
        } catch (e) {
            setError(friendly(e));
        } finally { setBusy(false); }
    };
    const onSignOff = async () => {
        if (!data) return;
        setBusy(true);
        try {
            const res = await reportsService.signOffWeek({ week_start: data.week_start, note: signNote });
            setData(d => d && ({ ...d, signoff: res.signoff }));
            setSignOpen(false);
            setToast('Week signed off');
        } catch (e) {
            setError(friendly(e));
        } finally { setBusy(false); }
    };
    // The same check the Monday mail carries, for the week on screen, to my own inbox.
    const onMail = async () => {
        if (!data) return;
        setMailing(true); setError(null);
        try {
            const res = await reportsService.mailWeeklyCheck(data.week_start);
            setToast(`Sent the ${weekLabel(data.week_start, data.week_end)} check to ${res.sent_to}`);
        } catch (e) {
            setError(friendly(e));
        } finally { setMailing(false); }
    };

    const counts = useMemo(() => Object.fromEntries(
        FILTERS.map(f => [f.key, (data?.machines || []).filter(f.test).length])), [data]);

    const machines = useMemo(() => {
        const rows = data?.machines || [];
        const needle = q.trim().toLowerCase();
        const pass = (FILTERS.find(f => f.key === filter) || FILTERS[0]).test;
        return rows.filter(m => {
            if (!pass(m)) return false;
            if (!needle) return true;
            const c = m.contracts[0] || {};
            return `${m.equipment_id} ${m.serial_number} ${m.model} ${c.customer} ${c.site} ${c.rental_contract_id}`.toLowerCase().includes(needle);
        });
    }, [data, q, filter]);

    const exportCsv = () => {
        if (!data) return;
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const head = ['Serial', 'Equipment ID', 'Model', 'Customer', 'Contract', 'Site', 'Data days', 'Frames', 'Engine on (h)', 'Meter (h)', 'Start hrs', 'End hrs', 'Auto check', 'Findings', 'Verdict', 'Checked by', 'Checked at', 'Note'];
        const lines = data.machines.map(m => {
            const c = m.contracts[0] || {};
            return [m.serial_number, m.equipment_id, m.model, c.customer, c.rental_contract_id, c.site,
                m.week.data_days, m.week.frames, m.week.ran_h, m.week.meter_h, m.week.start_eng, m.week.end_eng,
                AUTO[m.auto].label, m.findings.map(f => `${f.code}: ${f.message}`).join(' | '),
                m.review ? VERDICT[m.review.verdict].label : 'Not reviewed', m.review?.checked_by_name,
                m.review ? formatToIST(m.review.checked_at, 'yyyy-MM-dd HH:mm') : '', m.review?.note].map(esc).join(',');
        });
        const blob = new Blob([[head.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `weekly_data_check_${data.week_start}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };

    const s = data?.summary;
    const signed = data?.signoff;

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Tooltip title="Previous week">
                        <span><IconButton size="small" disabled={loading || !data} onClick={() => setWeek(data?.prev_week)}><PrevIcon /></IconButton></span>
                    </Tooltip>
                    <Typography variant="h6" fontWeight={800} sx={{ minWidth: 220, textAlign: 'center' }}>
                        {loading && !data ? 'Loading week…' : weekLabel(data?.week_start, data?.week_end)}
                    </Typography>
                    <Tooltip title={data?.next_week ? 'Next week' : 'This is the current week'}>
                        <span><IconButton size="small" disabled={loading || !data?.next_week} onClick={() => setWeek(data?.next_week)}><NextIcon /></IconButton></span>
                    </Tooltip>
                    {data?.is_current && <Chip size="small" color="info" label="Current week — in progress" sx={{ fontWeight: 700 }} />}
                </Stack>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                        size="small" placeholder="Search machine, customer, site…" value={q} onChange={e => setQ(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ width: 240 }}
                    />
                    <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_e, v) => v && setFilter(v)}>
                        {FILTERS.map(f => (
                            <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                {f.label}
                                {data && <Box component="span" sx={{ ml: 0.75, fontWeight: 600, color: 'text.secondary' }}>{counts[f.key]}</Box>}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Button size="small" variant="outlined" color="inherit" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!data}
                        sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>CSV</Button>
                    <Button size="small" variant="outlined" startIcon={<EmailIcon />} onClick={onMail} disabled={!data || loading || mailing}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {mailing ? 'Sending…' : 'Email me this week'}
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => load(true)} disabled={loading}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>Recompute</Button>
                    <Button size="small" variant="contained" startIcon={<SignIcon />} disabled={!data || loading} onClick={() => { setSignNote(signed?.note || ''); setSignOpen(true); }}
                        sx={{ textTransform: 'none', fontWeight: 700 }}>
                        {signed ? 'Sign off again' : 'Sign off week'}
                    </Button>
                </Stack>
            </Stack>

            {s && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2} alignItems="center">
                    <Chip size="small" label={`${s.machines} machines`} sx={{ fontWeight: 700 }} />
                    <Chip size="small" color="success" variant="outlined" label={`${s.ok} look right`} sx={{ fontWeight: 700 }} />
                    <Chip size="small" color="warning" label={`${s.warn} to check`} sx={{ fontWeight: 700 }} />
                    <Chip size="small" color="error" label={`${s.fail} with problems`} sx={{ fontWeight: 700 }} />
                    <Chip size="small" variant="outlined" label={`${s.reviewed}/${s.machines} reviewed`} sx={{ fontWeight: 700 }} />
                    {s.issues_open > 0 && <Chip size="small" color="error" variant="outlined" icon={<IssueIcon sx={{ fontSize: '15px !important' }} />} label={`${s.issues_open} open issues`} sx={{ fontWeight: 700 }} />}
                    <Box sx={{ flexGrow: 1 }} />
                    {signed ? (
                        <Chip size="small" color="success" icon={<SignIcon sx={{ fontSize: '15px !important' }} />}
                            label={`Signed off by ${signed.signed_by_name || '—'} · ${formatToIST(signed.signed_at, 'dd MMM, hh:mm a')}`} sx={{ fontWeight: 700 }} />
                    ) : (
                        <Chip size="small" variant="outlined" label="Not signed off" sx={{ fontWeight: 700, color: 'text.secondary' }} />
                    )}
                </Stack>
            )}

            {error && <Alert severity="error" sx={{ borderRadius: '16px', mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {toast && <Alert severity="success" sx={{ borderRadius: '16px', mb: 2 }} onClose={() => setToast(null)}>{toast}</Alert>}

            <Card>
                {loading ? (
                    <Box p={3}>{[...Array(6)].map((_, i) => <Skeleton key={i} height={44} />)}</Box>
                ) : machines.length === 0 ? (
                    <Box p={6} textAlign="center">
                        <Typography variant="body2" color="text.secondary" fontWeight={700}>
                            {!data ? 'This week could not be loaded'
                                : q || filter !== 'all' ? 'No machine matches that filter' : 'No working-fleet machines to check'}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    {[['MACHINE'], ['CONTRACT'], ['WEEK'], ['HOURS', 'right'], ['AUTO CHECK'], ['REVIEW'], ['', 'right']].map(([t, al], i) => (
                                        <TableCell key={i} align={al || 'left'} sx={{ fontWeight: 700, fontSize: '10px', color: 'text.secondary', whiteSpace: 'nowrap' }}>{t}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {machines.map(m => (
                                    <MachineRow key={m.equipment_id} m={m} weekStart={data.week_start} busy={busy}
                                        onReview={onReview} onClear={onClear}
                                        onOpen={(id) => navigate(`/mewp-detail/${encodeURIComponent(id)}`)} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                {DAY_LEGEND.map(([tone, label]) => (
                    <Stack key={label} direction="row" spacing={0.5} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: tone }} />
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Stack>
                ))}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                    Computed {data?.computed_at ? formatToIST(data.computed_at, 'dd MMM, hh:mm a') : '—'}
                </Typography>
            </Stack>

            <Dialog open={signOpen} onClose={() => !busy && setSignOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Sign off {weekLabel(data?.week_start, data?.week_end)}</DialogTitle>
                <DialogContent>
                    {s && s.flagged_pending > 0 && (
                        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                            {s.flagged_pending} flagged machine{s.flagged_pending === 1 ? '' : 's'} not reviewed
                        </Alert>
                    )}
                    {s && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {`${s.reviewed} of ${s.machines} machines reviewed · ${s.issues_open} open issue${s.issues_open === 1 ? '' : 's'}`}
                        </Typography>
                    )}
                    <TextField size="small" fullWidth multiline minRows={2} placeholder="Note for the week (optional)" value={signNote} onChange={e => setSignNote(e.target.value)} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSignOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" startIcon={<SignIcon />} onClick={onSignOff} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}>Sign off</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OperationsWeeklyDataCheck;
